import { useCallback, useEffect, useMemo, useState } from "react";
import { emitActivity, normalizeActivityPayload, subscribeToActivity } from "@/services/activity";
import type { Activity } from "@/types/activity";
import { filterActivitiesByActor, groupActivitiesBySection } from "@/utils/activityFeed";
import { useAuth } from "./useAuth";

export type ActivityFilter = "all" | "mine";

interface UseActivityFeedOptions {
  docId?: string;
  initialFilter?: ActivityFilter;
}

function upsertActivities(list: Activity[], incoming: Activity) {
  const seen = new Map(list.map((activity) => [activity.id, activity]));
  seen.set(incoming.id, incoming);

  return Array.from(seen.values()).sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

export function useActivityFeed({ docId = "global", initialFilter = "all" }: UseActivityFeedOptions = {}) {
  const { user } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [filter, setFilter] = useState<ActivityFilter>(initialFilter);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setActivities([]);
    setIsLoading(true);

    const unsubscribe = subscribeToActivity(docId, (activity) => {
      setActivities((prev) => upsertActivities(prev, activity));
      setIsLoading(false);
    });

    setIsLoading(false);

    return () => {
      unsubscribe();
    };
  }, [docId]);

  const addActivity = useCallback(
    (activity: Omit<Activity, "id" | "timestamp"> & Partial<Pick<Activity, "id" | "timestamp">>) => {
      const normalized = normalizeActivityPayload(activity);
      setActivities((prev) => upsertActivities(prev, normalized));
      emitActivity(docId, normalized);
      return normalized;
    },
    [docId]
  );

  const filteredActivities = useMemo(
    () => filterActivitiesByActor(activities, user?.id, filter),
    [activities, filter, user?.id]
  );

  const sections = useMemo(
    () => groupActivitiesBySection(filteredActivities),
    [filteredActivities]
  );

  return {
    activities: filteredActivities,
    allActivities: activities,
    sections,
    filter,
    setFilter,
    addActivity,
    isLoading,
  };
}
